/**
 * Esri Shapefile reader — .shp geometry, .dbf attributes, .prj projection.
 *
 * The format is parsed here by hand; the REPROJECTION is handed to proj4.
 *
 * WHY PROJ4 RATHER THAN MORE HAND-WRITTEN MATH
 * This module used to implement three projections itself and refuse everything
 * else — so an ordinary Lambert Conformal Conic file, which is what most
 * national and state datasets ship in, could not be imported at all. The fix
 * is not to hand-write LCC as well. Each projection is a page of series
 * expansions, every one is a chance to be subtly wrong, and a subtly wrong
 * reprojection is the worst possible outcome: the layer draws, looks
 * plausible, and sits a few hundred metres from where it belongs. proj4 is the
 * library the entire GIS world has already debugged, and it reads the ESRI WKT
 * out of a .prj directly.
 *
 * WHAT IS STILL HANDLED HERE
 * Geographic files skip proj4 entirely — they are already lon/lat, and pushing
 * a million coordinates through a transform to have it return them unchanged
 * is pure cost.
 *
 * DATUM SHIFTS ARE THE REMAINING LIMIT, and the import says so rather than
 * hiding it. A .prj that names a non-WGS84 datum without TOWGS84 parameters
 * cannot be shifted exactly — proj4 converts the ellipsoid and leaves the
 * datum where it is, which is typically right to within a few hundred metres.
 * Accurate enough to view; the notice tells the user when it applies.
 */

import { needsDatumShift, resolveCrs } from './reproject';

const SHP_HEADER_BYTES = 100;

/* ─────────────────────────────── Projection ─────────────────────────────── */

/**
 * How a file's coordinates become lon/lat.
 *
 *  - `geographic`  already degrees; used as-is
 *  - `projected`   anything proj4 could build a transform for
 *  - `unsupported` proj4 could not read the WKT at all
 */
export type Projection =
  | { kind: 'geographic' }
  | { kind: 'projected'; name: string; convert: (x: number, y: number) => [number, number] }
  | { kind: 'unsupported'; name: string; reason: string };

export function parsePrj(wkt: string | undefined): Projection {
  if (!wkt || !wkt.trim()) {
    // No .prj at all. Shapefiles in the wild are most often WGS84 lon/lat, and
    // the coordinate range check at the end catches it if this guess is wrong.
    return { kind: 'geographic' };
  }

  const resolved = resolveCrs(wkt);
  if (resolved.error) {
    return { kind: 'unsupported', name: resolved.name, reason: resolved.error };
  }
  // Already degrees — no transform to build, and none worth paying for on
  // every coordinate of a large file.
  if (!resolved.convert) return { kind: 'geographic' };

  return { kind: 'projected', name: resolved.name, convert: resolved.convert };
}

/** Converts one coordinate into WGS84 lon/lat. */
export function toLonLat(x: number, y: number, projection: Projection): [number, number] {
  switch (projection.kind) {
    case 'geographic':
      return [x, y];
    case 'projected':
      return projection.convert(x, y);
    default:
      // Unreachable: an unsupported projection is refused before parsing.
      return [x, y];
  }
}

/* ──────────────────────────────── .dbf ──────────────────────────────────── */

interface DbfField {
  name: string;
  type: string;
  length: number;
}

/** Reads the attribute table. Returns one plain object per record. */
export function parseDbf(bytes: Uint8Array): Record<string, unknown>[] {
  if (bytes.length < 32) return [];
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const decoder = new TextDecoder('latin1');

  const recordCount = view.getUint32(4, true);
  const headerLength = view.getUint16(8, true);
  const recordLength = view.getUint16(10, true);

  const fields: DbfField[] = [];
  for (let offset = 32; offset < headerLength - 1; offset += 32) {
    if (bytes[offset] === 0x0d) break; // field terminator
    const name = decoder.decode(bytes.subarray(offset, offset + 11)).replace(/\0.*$/, '').trim();
    if (!name) break;
    fields.push({
      name,
      type: String.fromCharCode(bytes[offset + 11]),
      length: bytes[offset + 16],
    });
  }

  const records: Record<string, unknown>[] = [];
  for (let index = 0; index < recordCount; index += 1) {
    let cursor = headerLength + index * recordLength;
    if (cursor + recordLength > bytes.length) break;
    // A leading '*' marks a deleted record.
    const deleted = bytes[cursor] === 0x2a;
    cursor += 1;

    const record: Record<string, unknown> = {};
    for (const field of fields) {
      const raw = decoder.decode(bytes.subarray(cursor, cursor + field.length)).trim();
      cursor += field.length;

      if (field.type === 'N' || field.type === 'F') {
        const value = Number(raw);
        record[field.name] = raw === '' || Number.isNaN(value) ? null : value;
      } else if (field.type === 'L') {
        record[field.name] = /^[YyTt]$/.test(raw) ? true : /^[NnFf]$/.test(raw) ? false : null;
      } else {
        record[field.name] = raw;
      }
    }

    if (!deleted) records.push(record);
  }

  return records;
}

/* ──────────────────────────────── .shp ──────────────────────────────────── */

/** Signed area; shapefile outer rings are clockwise (negative here). */
function ringArea(ring: [number, number][]): number {
  let sum = 0;
  for (let index = 0; index < ring.length - 1; index += 1) {
    sum += ring[index][0] * ring[index + 1][1] - ring[index + 1][0] * ring[index][1];
  }
  return sum / 2;
}

export function parseShp(bytes: Uint8Array, projection: Projection): (GeoJSON.Geometry | null)[] {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (view.getInt32(0, false) !== 9994) throw new Error('This is not a valid .shp file.');

  const geometries: (GeoJSON.Geometry | null)[] = [];
  let offset = SHP_HEADER_BYTES;

  const point = (at: number): [number, number] =>
    toLonLat(view.getFloat64(at, true), view.getFloat64(at + 8, true), projection);

  while (offset + 8 <= bytes.length) {
    const contentLength = view.getInt32(offset + 4, false) * 2; // 16-bit words
    let cursor = offset + 8;
    const type = view.getInt32(cursor, true);
    cursor += 4;

    if (type === 0) {
      geometries.push(null); // Null shape
    } else if (type === 1 || type === 11 || type === 21) {
      geometries.push({ type: 'Point', coordinates: point(cursor) });
    } else if (type === 8 || type === 18 || type === 28) {
      const count = view.getInt32(cursor + 32, true);
      const coords: [number, number][] = [];
      for (let index = 0; index < count; index += 1) coords.push(point(cursor + 36 + index * 16));
      geometries.push({ type: 'MultiPoint', coordinates: coords });
    } else if ([3, 5, 13, 15, 23, 25].includes(type)) {
      const partCount = view.getInt32(cursor + 32, true);
      const pointCount = view.getInt32(cursor + 36, true);
      const partsAt = cursor + 40;
      const pointsAt = partsAt + partCount * 4;

      const parts: number[] = [];
      for (let index = 0; index < partCount; index += 1) {
        parts.push(view.getInt32(partsAt + index * 4, true));
      }

      const rings: [number, number][][] = [];
      for (let part = 0; part < partCount; part += 1) {
        const from = parts[part];
        const to = part + 1 < partCount ? parts[part + 1] : pointCount;
        const ring: [number, number][] = [];
        for (let index = from; index < to; index += 1) ring.push(point(pointsAt + index * 16));
        if (ring.length > 0) rings.push(ring);
      }

      const isPolygon = type === 5 || type === 15 || type === 25;
      if (!isPolygon) {
        geometries.push(
          rings.length === 1
            ? { type: 'LineString', coordinates: rings[0] }
            : { type: 'MultiLineString', coordinates: rings },
        );
      } else {
        // Outer rings are clockwise; each one starts a new polygon and the
        // counter-clockwise rings that follow are its holes.
        const polygons: [number, number][][][] = [];
        for (const ring of rings) {
          if (ringArea(ring) < 0 || polygons.length === 0) polygons.push([ring]);
          else polygons[polygons.length - 1].push(ring);
        }
        geometries.push(
          polygons.length === 1
            ? { type: 'Polygon', coordinates: polygons[0] }
            : { type: 'MultiPolygon', coordinates: polygons },
        );
      }
    } else {
      geometries.push(null); // Unsupported shape type
    }

    offset += 8 + contentLength;
  }

  return geometries;
}

/* ────────────────────────────── Entry point ─────────────────────────────── */

export interface ShapefileResult {
  features: GeoJSON.Feature[];
  notices: string[];
  /** Base name of the .shp inside the archive, used as the layer name. */
  name: string;
}

export function buildShapefileFeatures(
  name: string,
  shp: Uint8Array,
  dbf: Uint8Array | undefined,
  prj: string | undefined,
): ShapefileResult {
  const projection = parsePrj(prj);
  if (projection.kind === 'unsupported') {
    throw new Error(
      `This shapefile's coordinate system ("${projection.name}") could not be read — ${projection.reason}. ` +
        'Check that the .prj file is complete, or reproject the data to WGS84 (EPSG:4326).',
    );
  }

  const geometries = parseShp(shp, projection);
  const attributes = dbf ? parseDbf(dbf) : [];
  const notices: string[] = [];

  if (!dbf) notices.push('No .dbf found — the layer has geometry but no attributes.');
  if (!prj) notices.push('No .prj found — coordinates were read as WGS84 longitude/latitude.');
  if (projection.kind === 'projected') {
    notices.push(`Reprojected from "${projection.name}" to WGS84.`);
    if (prj && needsDatumShift(prj)) {
      notices.push(
        'The .prj names a datum other than WGS84 and carries no transformation ' +
          'parameters, so positions may be out by up to a few hundred metres.',
      );
    }
  }

  const features: GeoJSON.Feature[] = [];
  let skipped = 0;

  geometries.forEach((geometry, index) => {
    if (!geometry) {
      skipped += 1;
      return;
    }
    features.push({
      type: 'Feature',
      id: index,
      geometry,
      properties: attributes[index] ?? {},
    });
  });

  if (skipped > 0) {
    notices.push(`${skipped} record${skipped === 1 ? '' : 's'} had no geometry and were skipped.`);
  }
  if (features.length === 0) throw new Error('This shapefile contains no usable geometry.');

  // A wrong .prj shows up as impossible degrees; better to say so than to draw
  // the layer somewhere meaningless.
  const sample = features.find((feature) => (feature.geometry as any)?.coordinates);
  const first = (() => {
    let value: any = (sample?.geometry as any)?.coordinates;
    while (Array.isArray(value) && Array.isArray(value[0])) value = value[0];
    return value as [number, number] | undefined;
  })();
  if (first && (Math.abs(first[0]) > 180 || Math.abs(first[1]) > 90)) {
    throw new Error(
      'Coordinates fall outside longitude/latitude range, so the .prj could not be applied. Reproject this shapefile to WGS84 (EPSG:4326) and try again.',
    );
  }

  return { features, notices, name };
}
